import { Injectable, signal } from '@angular/core';
import {
    asyncScheduler, BehaviorSubject, catchError, concat, concatMap, delay,
    EMPTY, expand, filter, from, map, Observable, of, Subject,
    subscribeOn, take, tap, throwError, toArray } from 'rxjs';
import { IPaginatedResponse } from '../../typing/response.interface';
import { IOrder, IOrderForm, IOrderPrice, IOrderPriceUpdate } from '../../typing/order.interface';
import { Order } from '../../classes/order.class';
import { CustomFieldsConfig } from '../../classes/custom-fields.class';
import { IItem } from '../../typing/item.interface';
import { IPrice } from '../../typing/price.interface';
import { IEquipmentDataFromDB, IProgressBarLoadingData } from './offline.interface';
import { IndexedDbService } from './IndexedDb.service';
import { CustomFieldsService } from '../../services/custom-fields.service';
import { ItemService } from '../../services/items.service';
import { PriceService } from '../../services/price.service';
import { OrdersService } from '../../services/orders.service';

@Injectable({
    providedIn: 'root'
})
export class OfflineService {
    customFieldsConfigs = new BehaviorSubject<IPaginatedResponse<CustomFieldsConfig> | null>(null);
    //customFieldsConfigs сохранения кастомных полей
    ordersList$ = new BehaviorSubject<Order[]>([]);
    //ordersList$ карточки Актуальные Неактуальные заявки
    ordersExecutor$ = new BehaviorSubject<IOrder[]>([]);
    //ordersExecutor$ для хранения данных всех форм страница order-executor
    prices$ = new BehaviorSubject<IPaginatedResponse<IPrice> | null>(null);
    private storeName = 'equipment';
    loadDateOrderOffline$ = new Subject<void>();

    offline = signal<boolean>(false);

    progressBarLoadingData = signal<IProgressBarLoadingData | null>(null);

    constructor(
        private indexedDbService: IndexedDbService,
        private customFieldsService: CustomFieldsService,
        private itemService:ItemService,
        private priceService: PriceService,
        protected ordersService: OrdersService,
    ) {}

    addOrderExecutor(newOrder: IOrder): void {
        const currentOrders = this.ordersExecutor$.value;
        if (!currentOrders.some(order => order.id === newOrder.id)) {
            this.ordersExecutor$.next([...currentOrders, newOrder]);
        }
    }

    getOrderExecutorCache(id: number): Observable<IOrder | undefined>{
        return this.ordersExecutor$.pipe(
            map((res) => res.find((item) => item.id === id)),
            take(1)
        );
    }

    updateOfflineOrdersList$(order: IOrder): void {
        const currentOrders = this.ordersList$.value;
        const updatedOrders = currentOrders.map(orderItem => {
            if (orderItem.data.id === order.id) {
                return {
                    ...orderItem,
                    data: {
                        ...orderItem.data,
                        status: order.status
                    }
                };
            }
            return orderItem;
        });
        this.ordersList$.next(updatedOrders as Order[]);
    }

    updateOfflineOrder(order?: Order[]): void {
        if (Array.isArray(order) && order.length > 0) {
            this.ordersList$.next(order);
        }
    }

    updateOfflineOrderExecutor(order: IOrder, formData: IOrderForm): void {
        const data = this.parserDataOrderExecutorOffline(order, formData);
        const currentOrder = this.ordersExecutor$.value.map((orders) => {
            orders.orderPrices.forEach(price => {
                formData.orderPrices.forEach( async (priceData, index  ) => {
                    if (price.price.id  === priceData.priceId) {
                        order.orderPrices[index] = await this.createOrderPrice(orders, priceData);
                    }
                });
            });

            return orders;
        });
        const updatedOrder = currentOrder.map(orderItem => orderItem.id === order.id ? { ...data } : orderItem);

        this.ordersExecutor$.next(updatedOrder);
    }

    private parserDataOrderExecutorOffline(order: IOrder, formData: IOrderForm): IOrder {
        return  {
            ...order,
            customFieldsParams: formData.customFieldsParams,
            status: formData.status,
            signature: formData.signature,
            conditionsCheck: formData.conditionsCheck,
        };
    }

    removePriceOrder(order: IOrder, idPrice: number): void {
        const currentOrder = this.ordersExecutor$.value.map((orders) => {
            orders.orderPrices =  orders.orderPrices.filter(price => price.id !== idPrice);
            return orders;
        });

        this.ordersExecutor$.next(currentOrder);
    }

    async addNewPriceOrder(order: IOrder, formData: IOrderPriceUpdate, index: number): Promise<void> {
        const orderPrice = await this.createOrderPrice(order, formData);
        order.orderPrices[index] = orderPrice;

        const orders =  this.ordersExecutor$.value.map((item) =>  {
            if (item.id === order.id)  {item.orderPrices = order.orderPrices;}
            return item;
        });

        /** для создания и редактирования работ и оборудования в режиме offline */

        this.ordersExecutor$.next(orders);
    }

    private async createOrderPrice(order: IOrder, formData: IOrderPriceUpdate): Promise<IOrderPrice> {
        const equipmentList = await this.getEquipmentList();
        const prices = this.prices$?.value?.data.find(item => item.id === formData.priceId);
        const currentEquipmentList = equipmentList?.data.find((item) => item.id === formData.itemId);

        return {
            quantity: formData.quantity,
            customPrice: formData.customPrice ?? null,
            price: {
                id: formData.priceId,
                price: formData?.priceValue || 0,
                discountPrice: formData?.discountPriceValue || 0,
                duration: 0,
                category: {
                    id: formData.itemId || 0,
                    name: currentEquipmentList?.name || '',
                },
                work: {
                    name: prices?.category?.name || '',
                },
            },
            item: {
                id: formData.itemId || 0,
                name: currentEquipmentList?.name || '',
            },
        } as IOrderPrice;

        /** для создания мокковых данных для редактирования работ и оборудования */

    }

    getCustomFieldsConfigsCache(): Observable<IPaginatedResponse<CustomFieldsConfig>> {
        return this.customFieldsConfigs.pipe(
            take(1),
            filter((res) => !!res)
        );
    }

    onSaveDataOfflineOrder(): Observable<boolean> {
        /** метод для сохраняем данных для offline режима */
        const currentDate = new Date().toLocaleDateString();
        const allOrders = this.ordersList$.value;
        const existingOrderIds = new Set(
            this.ordersExecutor$.value.map(order => order.id)
        );
        const ordersToFetch = allOrders.filter(
            order => !existingOrderIds.has(order.data.id)
        );

        let currentStep = 0;
        let totalPages = 0;

        if (ordersToFetch.length === 0) {
            totalPages = 2;
        }

        /** сохраняем карточки заказов */

        const orders$ = from(ordersToFetch).pipe(
            concatMap((order) => {

                if (ordersToFetch.length === 0) {
                    totalPages = 2;
                    return of(null);
                }
                /**Получаем карточек заказа */
                return this.ordersService.getOrder(order.data.id).pipe(
                    tap(fetchedOrder => {
                        totalPages = ordersToFetch.length + 2;
                        currentStep++;
                        this.addOrderExecutor(fetchedOrder);
                        ///сохраняем order-executor addOrderExecutor
                        this.progressBarLoadingData.set({
                            currentPage:currentStep,
                            totalPages,
                            isLoadingOrder: true,
                            isLoadingEquipment: false
                        });
                    }),
                    catchError((error) => {
                        return throwError(() => error);
                    }),
                );
            }),
            toArray(),
        );
        /** сохраняем кстомные филды */
        const customFieldsConfigs$ = of(this.customFieldsConfigs.value).pipe(
            concatMap((customFields) => {
                return !customFields ? this.customFieldsService.getCustomFieldsConfigs({}).pipe(
                    tap((customFields) => {
                        currentStep++;
                        this.progressBarLoadingData.set({
                            currentPage:currentStep, totalPages,
                            isLoadingOrder: true,
                            isLoadingEquipment: false
                        });
                        this.customFieldsConfigs.next(customFields);
                    }),
                    catchError((error) => {
                        return throwError(() => error);
                    })
                ) : of(null).pipe(tap(() => {
                    currentStep++;
                    this.progressBarLoadingData.set({
                        currentPage:currentStep,
                        totalPages,
                        isLoadingOrder: true,
                        isLoadingEquipment: false
                    });
                }));
            })
        );

        /** сохраняем цены */
        const prices$ = of(this.prices$.value).pipe(
            concatMap((prices) => {
                return !prices ? this.priceService.getList().pipe(
                    tap(prices => {
                        currentStep++;
                        this.progressBarLoadingData.set({
                            currentPage:currentStep,
                            totalPages,
                            isLoadingOrder: true,
                            isLoadingEquipment: false
                        });
                        this.prices$.next(prices);
                    }),
                    catchError((error) => {
                        return throwError(() => error);
                    })
                ):of(null).pipe(tap(() => {
                    currentStep++;
                    this.progressBarLoadingData.set({
                        currentPage:currentStep,
                        totalPages,
                    });
                }));
            })
        );
        /** сохраняем оборудования */
        const equipment$ = from(this.getLastEquipment()).pipe(
            concatMap((data) => {
                /**Получаем только последний элемент с indexDB */
                const meta = data?.equipment.meta || null;
                this.progressBarLoadingData.update((prev) => {
                    return {
                        currentPage: (prev?.totalPages || 0 ) + (meta?.totalPages || 0),
                        totalPages: (prev?.totalPages || 0 ) + (meta?.totalPages || 0),
                        isLoadingOrder: false,
                        isLoadingEquipment: true
                    };
                });
                /**meta сохраняем meta для прогрес бара */

                if (data && currentDate !== data.dateCreated) {
                    return from(this.deleteEquipmentDatabase()).pipe(
                        concatMap(() => of(null)) // возвращаем null после удаления
                    );
                    /**Проверяем текущую дату и сохранённую в IndexedDB. Если даты отличаются, очищаем IndexedDB. */
                }
                return of(data);
            }),
            concatMap((data) => {
                const counter: IProgressBarLoadingData = {
                    totalPages,
                    currentPage:currentStep,
                };
                return this.getEquipmentItems(data, currentDate, counter).pipe(
                    subscribeOn(asyncScheduler),
                    tap(async (items) => {
                        if(items) {
                            await this.updateEquipmentList(items, currentDate);
                        }
                    }),
                );
            }),
            toArray(),
        );
        /** Комбинируем все потоки последовательно*/
        return concat(customFieldsConfigs$, prices$, orders$, equipment$).pipe(
            toArray(),
            map((data) => data.flat().some((items) => items !== null)),
            /** Возвращает true если есть какие-либо новые данные для оффлайн-режима */
            /** false если все данные уже были актуальны и не требовали обновления*/
        );
    }

    getEquipmentItems(
        data: IEquipmentDataFromDB | null,
        currentDate: string,
        counter: IProgressBarLoadingData
    ): Observable<IPaginatedResponse<IItem> | null> {
        const limit = 100;
        let page = data?.equipment.meta.currentPage || 1;

        if (!data?.equipment?.links.next && data && currentDate === data.dateCreated) {
            /**Если ссылки на следующую страницу (next) нет, данные получены и дата создания данных совпадает с текущей датой,
             то возвращаем EMPTY (прерываем выполнение цепочки загрузки данных) */
            return of(null);
        }

        return this.itemService.getItems({page, limit}).pipe(
            expand((response) => {
                if(response.links.next) {
                    page = response.meta.currentPage + 1;
                    this.progressBarLoadingData.set({
                        totalPages: response.meta.totalPages + counter.totalPages,
                        currentPage:  counter.currentPage + page,
                        isLoadingOrder: false,
                        isLoadingEquipment: true
                    });
                }

                return response.links.next ? this.itemService.getItems({page, limit}).pipe(
                    delay(1000)
                ) : EMPTY;
            }),
        );
    }

    calculateProgress(data: IProgressBarLoadingData | null): number {
        if (!data) {return 0;}
        if (!data.totalPages || data.totalPages === 0) {return 0;}

        if (data.currentPage >= data.totalPages) {
            return 100;
        }

        // Если оборудование уже есть в IndexedDB и актуально
        if (data.isEquipmentCached) {
            return (data.currentPage / data.totalPages) * 100;
        }

        // Если грузятся только заказы, поля и цены (первая часть)
        if (data.isLoadingOrder && !data.isLoadingEquipment) {
            return (data.currentPage / data.totalPages) * 50;
        }

        // Если пошла загрузка оборудования (вторая часть)
        if (data.isLoadingEquipment) {
            // считаем прогресс как часть второй половины
            const halfProgress = (data.currentPage / data.totalPages) * 50;
            return 50 + halfProgress; // добавляем к уже загруженным 50%
        }

        return 0;
    }

    async updateEquipmentList(newEquipment: IPaginatedResponse<IItem>, dateCreated: string): Promise<void> {
        return this.indexedDbService.put(this.storeName, {
            id: newEquipment.meta.currentPage,
            equipment:newEquipment,
            dateCreated:dateCreated
        });
    }

    async getEquipmentList(): Promise<IPaginatedResponse<IItem> | null> {
        const data = await this.indexedDbService.getAll(this.storeName) as IEquipmentDataFromDB[];
        return data.reduce((acc, item) => {
            if (!acc.data) {
                acc.data = [];
            }
            acc.data.push(...item.equipment.data);
            acc.meta = item.equipment.meta;
            acc.links = item.equipment.links;
            return acc;
        }, {} as IPaginatedResponse<IItem>);
    }

    async deleteEquipmentDatabase(): Promise<void> {
        return this.indexedDbService.clear(this.storeName);
    }

    async getLastEquipment(): Promise<IEquipmentDataFromDB | null> {
        return this.indexedDbService.getLastItem(this.storeName);
    }
}
